import { Box, Card, CardContent, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import React, { useState } from "react";
import CardTitle from "./CardTitle";
import MiniField from "./MiniField";
import { useDispatch, useSelector } from "react-redux";
import { DataFilterTypes } from "../../common/common";
import { updateDataFilterSetting } from "../../reducer/CalcSettingsSlice";
import { numberSx, tokens } from "../../theme";

/**
 * One trace statistic: label left, value right, in the same label/monospace-value shape the Raw
 * Surface tab's threshold rows use.
 *
 * Replaces "Label (microns): 200" run together as inline text in two narrow columns, which wrapped
 * mid-pair in the right rail. Splitting label from value lets the value column align, and units
 * sit on the number rather than in the label, where they made the label long enough to wrap.
 */
function TraceStat({ label, value, unit, struck }) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            spacing={1}
            sx={struck ? { textDecoration: "line-through", opacity: 0.6 } : undefined}
        >
            <Typography variant="body2" sx={{ color: tokens.muted }}>{label}</Typography>
            <Typography variant="body2" sx={{ ...numberSx, whiteSpace: "nowrap" }}>
                {value}{unit ? <>&nbsp;{unit}</> : null}
            </Typography>
        </Stack>
    );
}

export default function NoiseReductionStepCard(props) {
    const laserData = useSelector(state => state.laserData);
    const calcSettings = useSelector(state => state.calcSettings);
    const [toleranceOverrideError, setToleranceOverride] = useState(false);
    const dispatch = useDispatch();
    const nrSettings = calcSettings.changes.dataFilters.find(df => df.type === DataFilterTypes.NoiseReductionStep); // 0 is the noise reduction filter

    const handleToleranceOverrideChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length !== 0) && (Number(evt.target.value) === 0);
            setToleranceOverride(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.NoiseReductionStep,
                property: "ToleranceOverride",
                value: evt.target.value,
                error: error
            }));
        }
    }

    const handleEnableChange = (evt) => {
        dispatch(updateDataFilterSetting({
            type: DataFilterTypes.NoiseReductionStep,
            enabledState: true,
            value: evt.target.checked,
            error: false,
        }));
    }

    const getTraceValue = (prop) => {
        try {
            let filter = laserData.filterTrace.filters.find(f => f.type === "NoiseReductionStep");
            if(filter) {
                return filter.trace[prop];
            } else {
                return -1;
            }
        } catch (err) {
            // Kept, unlike the debug logging removed around it: this is the only signal that a
            // trace lookup failed before the -1 fallback makes it look like absent data.
            console.error(err);
            return -1;
        }
    }

    const pointsRemoved = () => {
        let step = getTraceValue("step");
        let denominator = getTraceValue("denominator");
        if(denominator === -1) denominator = 10;
        let tolerance = getTraceValue("tolerance");
        let tolOverride = getTraceValue("toleranceoverride");
        let initial = getTraceValue("initialpointcount");
        let reduced = getTraceValue("reducedpointcount");

        if(initial > -1 && reduced > -1) {
            return (
                <>

                    {/* One column, not two: at rail width a two-column split left each pair too
                        narrow to hold "label: value" on one line. Stacking them keeps every value
                        in a single aligned column and removes the wrapping entirely. */}
                    <Stack spacing={0.5} sx={{ paddingTop: "5px" }}>
                        <TraceStat label="Step" value={step} unit="µm" struck={tolOverride !== -1} />
                        <TraceStat label="Denominator" value={denominator} struck={tolOverride !== -1} />
                        {(tolOverride !== -1) ? (
                            <TraceStat label="Tolerance override" value={tolOverride} unit="µm" />
                        ) : (
                            <TraceStat label="Tolerance" value={tolerance} unit="µm" />
                        )}
                        <TraceStat label="Initial points" value={initial} />
                        <TraceStat label="Points removed" value={initial - reduced} />
                        <TraceStat label="Remaining points" value={reduced} />
                    </Stack>
                </>
            )
        } else {
            return <></>
        }
    }

    const renderAttributes = () => {
        let toleranceOverride = (nrSettings.attributes.ToleranceOverride) ? nrSettings.attributes.ToleranceOverride : "";
        return (
            <Box>
                <Box display="flex" justifyContent="left" sx={{paddingLeft:"5px"}} >
                    <FormControlLabel 
                        label="Enabled" 
                        labelPlacement="end"
                        sx={{ 
                            '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem' // small font size (14px); you can also use 'small' keyword or a smaller value
                            }
                        }}
                        control={
                            <Switch 
                                checked={nrSettings.enabled} 
                                onChange={handleEnableChange}  
                                size="small"
                            /> 
                        }
                        >
                    </FormControlLabel>    
                </Box>
                {nrSettings.enabled && (
                    <>
                        <Box>
                            <MiniField
                                label="Tolerance Override (µm)"
                                value={toleranceOverride}
                                onChange={handleToleranceOverrideChange}
                                maxLength={4}
                                fullWidth
                                error={toleranceOverrideError}
                            />
                        </Box>
                        {pointsRemoved()}
                    </>
                )}
            </Box>
        );
    }
    return (
        <Card variant="outlined">
            <CardContent>
                <CardTitle title="Noise Reduction by Step" />
                {
                (nrSettings !== 3) ? (
                    renderAttributes()
                ) : 
                ("Not applied")
                }
            </CardContent>
        </Card>
    )
}